import { useMemo } from 'react'
import { Line } from '@react-three/drei'
import { ExtrudeGeometry, Quaternion, Shape, Vector3 } from 'three'
import type { EstadoExtensorOficina } from '../../dominio/didatico/oficinaExtensor'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { CilindroTelescopicoHolmatro3D } from './CilindroTelescopicoHolmatro3D'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { pontosRebatimentoPainel, poseExtensorEntrePontos, RECUO_TRASEIRO_CALCO_EXTENSOR } from './zonasRebatimentoPainel'
import type { IdPortaOficina } from './zonasOperacaoPorta'

function ApoioDoExtensor3D({ posicao }: { posicao: [number, number, number] }) {
  const geometria = useMemo(() => {
    const forma = new Shape()
    forma.moveTo(-RECUO_TRASEIRO_CALCO_EXTENSOR, 0); forma.lineTo(0.24, 0); forma.lineTo(0.23, 0.10)
    forma.lineTo(0.10, 0.12); forma.lineTo(0.09, 0.19); forma.lineTo(-0.04, 0.21)
    forma.lineTo(-0.05, 0.28); forma.lineTo(-0.19, 0.31); forma.lineTo(-RECUO_TRASEIRO_CALCO_EXTENSOR, 0)
    return new ExtrudeGeometry(forma, { depth: 0.018, bevelEnabled: false })
  }, [])
  return <group name="apoio-extensor-coluna-b" position={[posicao[0], posicao[1] - 0.16, posicao[2]]}>
    {[-0.065, 0.065].map((x) => <mesh key={x} geometry={geometria} position={[x, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
      <meshStandardMaterial color="#cbd5e1" metalness={0.85} roughness={0.28} />
    </mesh>)}
    {[-0.15, 0, 0.16].map((z, i) => <mesh key={z} position={[0, 0.03 + i * 0.06, z]} rotation={[0, 0, Math.PI / 2]}>
      <cylinderGeometry args={[0.017, 0.017, 0.15, 12]} /><meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.3} />
    </mesh>)}
    <mesh position={[0, 0.018, -RECUO_TRASEIRO_CALCO_EXTENSOR + 0.48 / 2]}><boxGeometry args={[0.18, 0.035, 0.48]} /><meshStandardMaterial color="#64748b" metalness={0.7} roughness={0.4} /></mesh>
  </group>
}

export function RebatimentoPainel3D({ perfil, idPorta, estado, ferramentaVisivel, afastamento = 0 }: {
  perfil: PerfilVeiculo; idPorta: IdPortaOficina; estado: EstadoExtensorOficina; ferramentaVisivel: boolean; afastamento?: number
}) {
  const rebatimento = estado.rebatimento
  if (!rebatimento) return null
  const { baseExtensor, cabecaExtensor, sinal } = pontosRebatimentoPainel(perfil, idPorta, estado.deslocamentoPainel)
  const base = new Vector3(...baseExtensor), cabeca = new Vector3(...cabecaExtensor)
  const direcao = cabeca.clone().sub(base).normalize()
  const pose = poseExtensorEntrePontos(baseExtensor, cabecaExtensor)
  const origem = base.clone().addScaledVector(direcao, pose.offsetOrigem)
  const giro = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), direcao)
  return <group name="rebatimento-painel">
    {rebatimento.apoioInstalado && <ApoioDoExtensor3D posicao={baseExtensor} />}
    {(rebatimento.extensorPosicionado || ferramentaVisivel) && <group position={[sinal * (rebatimento.extensorPosicionado ? 0 : afastamento), 0, 0]}>
      <group name="extensor-apoiado-b-a" position={origem} quaternion={giro} scale={pose.escala}>
        <CilindroTelescopicoHolmatro3D progresso={pose.progresso} ativa={estado.deslocamentoPainel > 0} />
      </group>
      <Line points={[baseExtensor, cabecaExtensor]} color="#55d98a" lineWidth={2} transparent opacity={0.6} />
    </group>}
    {rebatimento.vitimaLiberada && <Rotulo3D posicao={[(baseExtensor[0] + cabecaExtensor[0]) / 2, (baseExtensor[1] + cabecaExtensor[1]) / 2 + 0.3, (baseExtensor[2] + cabecaExtensor[2]) / 2]}
      texto="Vítima liberada" subtexto="Espaço para extração" variante="achado" sempreVisivel fator={3} />}
  </group>
}
